import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import RecordsListPage from './pages/RecordsListPage';
import RecordDetailPage from './pages/RecordDetailPage';
import RecordFormPage from './pages/RecordFormPage';
import InspectionsListPage from './pages/InspectionsListPage';
import InspectionFormPage from './pages/InspectionFormPage';
import RecertificationPage from './pages/RecertificationPage';
import CertificateActionsPage from './pages/CertificateActionsPage';
import BulkUploadPage from './pages/BulkUploadPage';
import PublicVerifyPage from './pages/PublicVerifyPage';
import AdminApprovalsPage from './pages/admin/AdminApprovalsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminAuditLogsPage from './pages/admin/AdminAuditLogsPage';
import AdminConfigPage from './pages/admin/AdminConfigPage';
import DeletedRecordsLogPage from './pages/admin/DeletedRecordsLogPage';
import ReportsPage from './pages/ReportsPage';
import InspectorPerformancePage from './pages/InspectorPerformancePage';
import WorkshopsPage from './pages/WorkshopsPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      
      {/* Public Verification (no auth required) */}
      <Route path="/verify/:token" element={<PublicVerifyPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* Records */}
      <Route path="/records" element={<ProtectedRoute><RecordsListPage /></ProtectedRoute>} />
      <Route path="/records/new" element={<ProtectedRoute roles={['admin', 'inspector']}><RecordFormPage /></ProtectedRoute>} />
      <Route path="/records/:id" element={<ProtectedRoute><RecordDetailPage /></ProtectedRoute>} />
      <Route path="/records/:id/edit" element={<ProtectedRoute roles={['admin', 'inspector']}><RecordFormPage /></ProtectedRoute>} />
      
      {/* Inspections */}
      <Route path="/inspections" element={<ProtectedRoute><InspectionsListPage /></ProtectedRoute>} />
      <Route path="/inspections/new" element={<ProtectedRoute roles={['admin', 'inspector']}><InspectionFormPage /></ProtectedRoute>} />
      <Route path="/inspections/:id/edit" element={<ProtectedRoute roles={['admin', 'inspector']}><InspectionFormPage /></ProtectedRoute>} />
      <Route path="/inspections/recertify" element={<ProtectedRoute roles={['admin', 'inspector']}><RecertificationPage /></ProtectedRoute>} />
      <Route path="/inspections/bulk-upload" element={<ProtectedRoute roles={['inspector']}><BulkUploadPage /></ProtectedRoute>} />
      <Route path="/inspections/:inspectionId/certificate-actions" element={<ProtectedRoute roles={['admin', 'inspector']}><CertificateActionsPage /></ProtectedRoute>} />
      
      {/* Reports */}
      <Route path="/reports" element={<ProtectedRoute roles={['admin', 'regulator']}><ReportsPage /></ProtectedRoute>} />
      
      {/* Workshops */}
      <Route path="/workshops" element={<ProtectedRoute><WorkshopsPage /></ProtectedRoute>} />
      
      {/* Performance Dashboard */}
      <Route path="/performance" element={<ProtectedRoute><InspectorPerformancePage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin/approvals" element={<ProtectedRoute roles={['admin']}><AdminApprovalsPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute roles={['admin', 'regulator']}><AdminAuditLogsPage /></ProtectedRoute>} />
      <Route path="/admin/deleted-records" element={<ProtectedRoute roles={['admin', 'regulator']}><DeletedRecordsLogPage /></ProtectedRoute>} />
      <Route path="/admin/config" element={<ProtectedRoute roles={['admin']}><AdminConfigPage /></ProtectedRoute>} />

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
